import { useMemo } from "react";
import ConfigureLink from "../../components/HelpCentre/ConfigureLink";
import HelpComingSoon from "../../components/HelpCentre/HelpComingSoon";
import HelpSearchBox from "../../components/HelpCentre/HelpSearchBox";
import HelpState from "../../components/HelpCentre/HelpState";
import TopicGrid from "../../components/HelpCentre/TopicGrid";
import {
	useHelpSearchIndex,
	useReaderTopics,
	useSearchQueryParam,
} from "../../hooks/useHelpCentre";
import { helpConfigurationPath } from "../../utils/helpCentre/content";
import { matchingTopicKeys, queryTerms } from "../../utils/helpCentre/search";

const topicCount = (count: number) =>
	`${count} ${count === 1 ? "topic" : "topics"}`;

/** Every topic, with a search that narrows the cards to topics with a match anywhere in them. */
function HelpCentreTopics() {
	const { topics, isError, isFetching, refetch } = useReaderTopics();
	const index = useHelpSearchIndex(topics);
	const { input, setInput, query } = useSearchQueryParam();
	const terms = useMemo(() => queryTerms(query), [query]);
	const isSearching = terms.length > 0;

	const shown = useMemo(() => {
		if (!topics || !isSearching) return topics;
		const keys = matchingTopicKeys(index, terms);
		return topics.filter((topic) => keys.has(topic.key));
	}, [topics, index, terms, isSearching]);

	return (
		<>
			<div className="help-page-header">
				<div className="help-page-header__inner">
					<div className="help-page-header__text">
						<h1 className="help-page-header__title">Topics</h1>
						<p className="help-page-header__subtitle">Browse all help topics</p>
					</div>
					<div className="help-page-header__actions">
						<HelpSearchBox
							value={input}
							onChange={setInput}
							label="Search topics"
							placeholder="Search topics"
						/>
						<ConfigureLink
							label="Configure topics"
							to={helpConfigurationPath()}
						/>
					</div>
				</div>
			</div>

			<div className="help-body help-body--wide">
				{isError ? (
					<HelpState
						onRetry={() => refetch()}
						retrying={isFetching}
						message="We couldn't load the topics."
					/>
				) : (
					<TopicGrid
						topics={shown}
						skeletonCount={9}
						empty={
							isSearching ? (
								<p className="help-empty">No topics match “{query.trim()}”.</p>
							) : (
								<HelpComingSoon />
							)
						}
					/>
				)}
			</div>

			<output className="sr-only">
				{isSearching && shown ? `${topicCount(shown.length)} found` : ""}
			</output>
		</>
	);
}

export default HelpCentreTopics;
